import BarChart from '@App/components/BarChart/BarChart';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { RISK_DATA_KASUNGU } from '@App/pages/RiskDataPage/risk-data';

import DefinitionComponent from '@App/components/DefinitionComponent/DefinitionComponent';

import { GLOSSARY_ITEMS } from '@App/components/Navbar/GlossarySearch/glossary-search.constants';
import RiskDataStories from '../RiskDataStories/RiskDataStories';

import { getMaxValue } from '../risk-data-content.utils';

import './risk-data-fire.styl';

function RiskDataFire({
  selectedScenario,
  selectedHorizon,
}) {
  const { t } = useTranslation();

  return (
    <div className="risk-data-fire">
      <h3>{t('Introduction')}</h3>
      <p>
        <DefinitionComponent
          id={73}
          term="Wildfire"
          definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
          className="my-sixth-risk-step"
        />
        { ' '}
        {t('and urban fires describe an uncontrolled burning fire, usually originated in natural areas, which can cause damage to forestry, agriculture, infrastructure and buildings. Wildfires that occur naturally are usually triggered by extreme heat and')}
        { ' '}
        <DefinitionComponent
          id={23}
          term="Drought"
          definition={GLOSSARY_ITEMS.find(item => item.id === 23)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 23)?.url}
          lowercase
        />
        { ' '}
        {t('conditions, but also by lightning storms. ')}
        <DefinitionComponent
          id={10}
          term="Climate change"
          definition={GLOSSARY_ITEMS.find(item => item.id === 10)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 10)?.url}
        />
        {t(' is leading to warmer, drier conditions that therefore amplify fire risk.')}
      </p>
      <BarChart
        data={RISK_DATA_KASUNGU.fire[selectedScenario.id]}
        selectedItem={selectedHorizon.id}
        title={t('Number of days per year with very high fire danger in the scenario of {{selectedScenario}}', { selectedScenario: t(selectedScenario.label).toLowerCase() })}
        xDataKey="horizon"
        yAxisLabel={t('Days/year')}
        yDataKey="value"
        yDomain={[0, getMaxValue(RISK_DATA_KASUNGU.fire)]}
      />
      <p>
        {t('The fire danger is estimated using the <strong>fire weather index</strong>, an index that evaluates favourable meteorological conditions to triggering a wildfire. The higher the index, the higher the risk of encountering ')}
        <DefinitionComponent
          id={72}
          term="Weather"
          definition={GLOSSARY_ITEMS.find(item => item.id === 72)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 72)?.url}
          lowercase
        />
        {t(' that could support the initiation and propagation of a wildfire. When the fire weather index is larger than 38, the fire danger is very high or extreme.')}
      </p>
      <p>
        {t('Over the last thirty years (1995-2024), the average number of days when the fire danger was very high in Kasungu was 52. In the future, we expect more days of the year with a high fire weather risk, more than 20% more days on average over the next 30 years (2025-2054). In the long term (2055-2084) it will add up to 74-85 days (depending on the scenario) signalling that wildfires are projected to become more frequent under ')}
        <DefinitionComponent
          id={10}
          term="Climate change"
          definition={GLOSSARY_ITEMS.find(item => item.id === 10)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 10)?.url}
          lowercase
        />
        .
      </p>
      <div className="how-does-this-affect-me__wrapper">
        <h3>
          {t('How does this affect me?')}
        </h3>
        <p>
          <DefinitionComponent
            id={73}
            term="Wildfires"
            definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
          />
          {t(' are not a major cause of death, but they can be very destructive. Many ')}
          <DefinitionComponent
            id={73}
            term="Wildfires"
            definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
            lowercase
          />
          {t(' are caused by human activities, either accidentally or as a consequence of carelessness, or arson. ')}
          <DefinitionComponent
            id={73}
            term="Wildfire"
            definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
          />
          {t(' occurrence, characteristics and impacts are closely linked to other hazards: ')}
          <DefinitionComponent
            id={23}
            term="Droughts"
            definition={GLOSSARY_ITEMS.find(item => item.id === 23)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 23)?.url}
            lowercase
          />
          {t(', heat waves and extreme ')}
          <DefinitionComponent
            id={72}
            term="Weather"
            definition={GLOSSARY_ITEMS.find(item => item.id === 72)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 72)?.url}
            lowercase
          />
          {t(' events can influence fire intensity and severity. ')}
          <DefinitionComponent
            id={73}
            term="Wildfires"
            definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
          />
          {t(' can create secondary hazards such as post-fire landslides, mudslides, flash ')}
          <DefinitionComponent
            id={31}
            term="Floods"
            definition={GLOSSARY_ITEMS.find(item => item.id === 31)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 31)?.url}
            lowercase
          />
          {', '}
          <DefinitionComponent
            id={60}
            term="Erosion"
            definition={GLOSSARY_ITEMS.find(item => item.id === 60)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 60)?.url}
            lowercase
          />
          {t(' and siltation.')}
        </p>
      </div>
      <h3>
        {t('How can we adapt?')}
      </h3>
      <p>
        {t('Fire is frequently used for land clearing for agriculture and creation of charcoal. Use sustainable forest management and fire risk minimisation activities to mitigate these risks. Educate communities and raise public awareness about the risk of ')}
        <DefinitionComponent
          id={73}
          term="Wildfires"
          definition={GLOSSARY_ITEMS.find(item => item.id === 73)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 73)?.url}
          lowercase
        />
        {'. '}
        {t('Consider fire risk when planting vegetation.')}
        {t('<br/> Take into account fire risk when planting vegetation.')}
      </p>
      <RiskDataStories hazardCategories={['fire']} />
    </div>
  );
}

export default RiskDataFire;